import { type ReactNode, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Clickable from 'components/Common/Clickable'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LinePlaneIcon from 'LuxKit/Icons/line/LinePlaneIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineCarSideviewIcon from 'LuxKit/Icons/line/LineCarSideviewIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import { getCheckInCheckOutText } from 'components/Search/utils'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import moment from 'moment'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { getOrderTileTitle } from './bookingSelectionUtils'
import { formatOrderTileOccupancyLabel } from './orderTileDisplayUtils'

type DisplayVariant = 'default' | 'addedToTrip'
const ADDED_TO_TRIP_CLASS_NAME = 'added-to-trip'
const SUBTITLE_SEPARATOR = '\u00b7'

const Container = styled(Clickable)`
  width: ${rem(240)};
  min-height: ${rem(110)};
  flex-shrink: 0;

  &.${ADDED_TO_TRIP_CLASS_NAME} {
    width: ${rem(300)};
    min-height: ${rem(68)};
  }

  > * {
    height: 100%;
  }
`

const ThumbnailWrapper = styled.div`
  width: ${rem(44)};
  height: ${rem(58)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  flex-shrink: 0;

  &.${ADDED_TO_TRIP_CLASS_NAME} {
    width: ${rem(48)};
    height: ${rem(48)};
  }
`

const CompactContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  min-width: 0;
`

const OrderTypeIconList = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
  flex-shrink: 0;
`

const OrderTypeIconSeparator = styled.span`
  color: ${props => props.theme.palette.neutral.default.three};
  font-size: ${rem(12)};
  line-height: 1;
`

const OrderTypeIconItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
`

function getOrderTypeIcon(typeId: string): ReactNode {
  switch (typeId) {
    case 'accommodation':
    case 'hotel':
      return <LineBedIcon size="XS" colour="light-foreground-alt" />
    case 'flights':
    case 'flight':
      return <LinePlaneIcon size="XS" colour="light-foreground-alt" />
    case 'experiences':
    case 'experience':
      return <LineTicketIcon size="XS" colour="light-foreground-alt" />
    case 'transfers':
    case 'transfer':
      return <LineCarSideviewIcon size="XS" colour="light-foreground-alt" />
    case 'carHire':
    case 'car_hire':
      return <LineCarIcon size="XS" colour="light-foreground-alt" />
    case 'cruises':
    case 'cruise':
      return <LineShipIcon size="XS" colour="light-foreground-alt" />
    case 'tours':
    case 'tour':
      return <LineMapIcon size="XS" colour="light-foreground-alt" />
    default:
      return null
  }
}

interface Props {
  itemId: string
  propertyName: string
  additionalBookings: number
  orderTypeIds?: Array<string>
  checkInDate?: string
  checkOutDate?: string
  occupancy?: App.Occupants
  imageId?: string
  showOccupancy?: boolean
  selected?: boolean
  disabled?: boolean
  displayVariant?: DisplayVariant
  onClick: (itemId: string) => void
}

export default function ChatLandingUpcomingOrderTile({
  itemId,
  propertyName,
  additionalBookings,
  orderTypeIds,
  checkInDate,
  checkOutDate,
  occupancy,
  imageId,
  showOccupancy = true,
  selected,
  disabled = false,
  displayVariant = 'default',
  onClick,
}: Props) {
  const intl = useIntl()
  const isAddedToTripVariant = displayVariant === 'addedToTrip'
  const displayVariantClassName = isAddedToTripVariant ? ADDED_TO_TRIP_CLASS_NAME : undefined
  const title = useMemo(() => getOrderTileTitle(intl, {
    propertyName,
    additionalBookings,
  }), [additionalBookings, intl, propertyName])
  const orderTypeIcons = useMemo(() => {
    return (orderTypeIds ?? [])
      .map(typeId => ({ typeId, icon: getOrderTypeIcon(typeId) }))
      .filter((item): item is { typeId: string, icon: ReactNode } => Boolean(item.icon))
  }, [orderTypeIds])
  const subtitle = useMemo(() => {
    const parts: Array<string> = []
    if (checkInDate && checkOutDate) {
      parts.push(getCheckInCheckOutText(checkInDate, checkOutDate))
    } else if (checkInDate) {
      parts.push(moment(checkInDate).format(DMY_CASUAL_SHORT_FORMAT))
    }
    if (showOccupancy) {
      const occupancyLabel = formatOrderTileOccupancyLabel(intl, occupancy)
      if (occupancyLabel) {
        parts.push(occupancyLabel)
      }
    }
    return parts.join(' · ')
  }, [checkInDate, checkOutDate, occupancy, showOccupancy, intl])

  const yourBookingLabel = useMemo(() => intl.formatMessage({
    id: 'dNPOURpXZmUVjbqyPKX3',
    defaultMessage: 'Your booking',
    description: 'Category label for upcoming booking tile on the chat landing page',
  }), [intl])

  const handleClick = useCallback(() => {
    if (disabled) {
      return
    }

    onClick(itemId)
  }, [disabled, onClick, itemId])

  const tileContent = (
    <Group
      direction="horizontal"
      gap={isAddedToTripVariant ? 8 : 12}
      verticalAlign="center"
      grow
    >
      {imageId && <ThumbnailWrapper className={displayVariantClassName}>
        <ResponsiveImage id={imageId} alt={propertyName} fit="center" objectFit="cover" />
      </ThumbnailWrapper>}
      <Group direction="vertical" gap={2} grow>
        <BodyText
          variant="medium"
          weight="normal"
          lineClamp={isAddedToTripVariant ? 1 : 2}
        >
          {title}
        </BodyText>
        {(orderTypeIcons.length > 0 || subtitle) && <SubtitleRow>
          {orderTypeIcons.length > 0 && <OrderTypeIconList aria-hidden="true">
            {orderTypeIcons.map(({ typeId, icon }, index) => (
              <OrderTypeIconItem key={typeId}>
                {index > 0 && <OrderTypeIconSeparator>{SUBTITLE_SEPARATOR}</OrderTypeIconSeparator>}
                {icon}
              </OrderTypeIconItem>
            ))}
          </OrderTypeIconList>}
          {orderTypeIcons.length > 0 && subtitle && <Caption variant="medium" colour="light-foreground-alt">{SUBTITLE_SEPARATOR}</Caption>}
          {subtitle && <Caption variant="medium" colour="light-foreground-alt" lineClamp={1}>
            {subtitle}
          </Caption>}
        </SubtitleRow>}
      </Group>
    </Group>
  )

  const compactContent = (
    <CompactContent>{tileContent}</CompactContent>
  )

  const containerContent = isAddedToTripVariant ? compactContent : (
    <Group direction="vertical" gap={12} verticalAlign="center" fullHeight>
      <CaptionTextBlock
        variant="small"
        weight="bold"
        format="uppercase"
        colour="light-foreground-alt"
        startIcon={<LineCalendarIcon size="XS" />}
      >
        {yourBookingLabel}
      </CaptionTextBlock>
      {tileContent}
    </Group>
  )

  return (
    <Container
      onClick={handleClick}
      disabled={disabled}
      className={displayVariantClassName}
    >
      <Sheet
        background="normal-background"
        border={selected ? 'normal-contrast' : 'light-foreground'}
        borderRadius="L"
        padding={isAddedToTripVariant ? 6 : 12}
      >
        {containerContent}
      </Sheet>
    </Container>
  )
}
